import { FC, useState } from "react";
import { Redirect } from "react-router-dom";
import CreateProject from "@app/projects/create/createProject";
import getOptions from "@app/projects/create/data/options";
import { FieldGroup } from "@app/projects/create/interfaces";
import WizardStep from "@app/projects/create/WizardStep";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { UserContext } from "@app/user/UserContext";

interface CreateProjectStepOptionsProps {
  baseUrl: string;
  completionTracker: boolean[];
  createProjectModel: CreateProject;
  handleUpdate: (property: UpdateResourceKeys) => void;
  hideClosestProviderReportOption: boolean;
  hideCMSApplicationCredit: boolean;
  hideCreateStandards: boolean;
  hidePracAtLocCondifence: boolean;
  hideProviderClaimsActivity: boolean;
  isDataShareEligible: boolean;
  isZipProject: boolean;
  onNextClick: () => void;
  progressList: string[];
  currentIndex: number;
  hideEnterpriseComplianceDashboard: boolean;
  hideMotiveAppropriatenessScore: boolean;
  hideStrictTimeDistance: boolean;
}

type UpdateResourceKeys = keyof CreateProject;

const CreateProjectStepOptions: FC<CreateProjectStepOptionsProps> = (props: CreateProjectStepOptionsProps) => {
  const [restartApp] = useState(!props.completionTracker.filter((_, index) => (props.currentIndex - 1) > index).every(step => step));

  if (restartApp) {
    return <Redirect to={`${props.baseUrl}/1`} />;
  }

  const handleIsNextDisabled = () => {
    return false;
  }

  return (
    <WizardStep {...props} heading="Create a New Project" subheading="Project Options" baseUrl={props.baseUrl} prevUrl={(props.currentIndex - 1).toString()} nextUrl={(props.currentIndex + 1).toString()} progressList={props.progressList} isNextDisabled={handleIsNextDisabled()} onNextClick={props.onNextClick}>
      <FeatureFlagContext.Consumer>
        {({ enabledFeatureFlags }) => (
          <UserContext.Consumer>
            {(user) => (
              getOptions<CreateProject>(
                {
                  accuracyAndAdequacy: props.createProjectModel.accuracyAndAdequacy,
                  affiliationsDisplayOnNetworkAccordion: props.createProjectModel.affiliationsDisplayOnNetworkAccordion,
                  enableNetworkIntegrityOption: props.createProjectModel.adequacyTemplateCollectionId != null && props.createProjectModel.accuracyTemplateCollectionId == null,
                  enablePatternOfCareProvidersOption: props.createProjectModel.hasZipSummaries && props.createProjectModel.showNextClosestNetworkProviders,
                  enablePgrConfidenceTierOption: props.createProjectModel.adequacyTemplateCollectionId != null,
                  enableRegulatoryExceptionReportsOption: props.createProjectModel.hasZipSummaries && props.createProjectModel.showNextClosestNetworkProviders,
                  hideCMSApplicationCredit: props.hideCMSApplicationCredit,

                  hideClosestProviderReport: props.hideClosestProviderReportOption,
                  hideCreateStandards: props.hideCreateStandards,
                  hideEnterpriseComplianceDashboard: props.hideEnterpriseComplianceDashboard,
                  hideMotiveAppropriatenessScore: props.hideMotiveAppropriatenessScore,
                  hidePciData: !props.createProjectModel.adequacyTemplateCollectionId,
                  hidePracAtLocCondifence: props.hidePracAtLocCondifence,
                  hideProviderClaimsActivity: props.hideProviderClaimsActivity,
                  hideRppoNonContractedProvidersIncludedInHsd: !enabledFeatureFlags.has(Features.RPPO_NON_PAR_PROVIDER_TO_HSD_TABLE_INCLUSION),
                  hideSnowflakeProjectRunProviderSets: !enabledFeatureFlags.has(Features.SNOWFLAKE_PROJECT_RUN_PROVIDER_SETS),
                  hideStrictTimeDistance: props.hideStrictTimeDistance,
                  isDataShareEligible: props.isDataShareEligible,
                  isZipProject: props.isZipProject,
                  showAffiliationsInsights: props.createProjectModel.showAffiliationsInsights,
                  showNcpInStateProvidersOnly: enabledFeatureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS) && props.createProjectModel.showNextClosestNetworkProviders,
                  showNetworkIntegrityDashboardChecked: props.createProjectModel.showNetworkIntegrityDashboard,
                  showPciDataChecked: props.createProjectModel.showPciData,
                  showZipAnalysisNextClosestProviders: enabledFeatureFlags.has(Features.ZIP_NEXT_CLOSEST_PROVIDERS),
                  showZipGapAgeTracking: enabledFeatureFlags.has(Features.ZIP_GAP_AGE_TRACKING),
                  useAccuracy: props.createProjectModel.accuracyTemplateCollectionId !== null,
                  userRoles: user.roles,
                  zipVolumeTargetingEnabled: enabledFeatureFlags.has(Features.ZIP_CODE_ANALYSIS_VOLUME_TARGETING),
                }
              ).map((fieldset: FieldGroup<CreateProject>) => {
                if (fieldset.fields.length > 0) {
                  return (
                    <div className="col-12 col-sm-5 d-inline-block align-top mt-5" key={fieldset.title}>
                      <h3>{fieldset.title}</h3>
                      {fieldset.fields.map((option) => (
                        <div className="row" key={option.key}>
                          <div className="col-12">
                            <TooltipWrapper text={option.tooltip} tooltipWidth="20rem">
                              <QESCheckbox
                                containerClassName={!!option.dependency ? "ms-4" : ""}
                                className="custom-control-inline"
                                checked={!!props.createProjectModel[option.key as keyof CreateProject]}
                                disabled={option.disabled}
                                testId={"create-project-step-options__" + option.key + "-checkbox"}
                                onChange={() => {
                                  props.handleUpdate(
                                    option.key as keyof CreateProject,
                                  );
                                }}
                                label={option.title}
                              />
                            </TooltipWrapper>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }
              })
            )}
          </UserContext.Consumer>
        )}
      </FeatureFlagContext.Consumer>
    </WizardStep>
  );
}

export default CreateProjectStepOptions;
